/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import { Box, Chip, Typography, alpha, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import {
    WA_GREEN,
    WA_BG,
    WA_BUBBLE,
    getUsageTypeColor,
    formatTemplateName,
    formatUsageType,
    renderTextWithVariables,
    getTemplateHeader,
    getTemplateBody,
    getTemplateFooter,
    getTemplateButtons,
    getTemplateVariables,
} from './whatsAppTemplateUtils';

// ----------------------------------------------------------------------
// Purely presentational phone mockup — no modal, no list, no action buttons.
// Renders the WhatsApp template preview: header bar, chat bubble, CTA buttons,
// and an optional variable legend. Compose with your own footer as needed.
// ----------------------------------------------------------------------

export default function WhatsAppTemplatePreviewPanel({
    template,
    contextMapping,
    showVariables,
    hideTitle,
    sx,
}) {
    const theme = useTheme();

    if (!template) {
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    gap: 1,
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: theme.palette.grey[100],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon icon="mdi:message-text-outline" width={28} color={theme.palette.grey[300]} />
                </Box>
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                    Select a template to preview
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
                    Use arrow keys to navigate, Enter to send
                </Typography>
            </Box>
        );
    }

    // Default to {} so the variable legend never indexes into undefined for a
    // template that has {{N}} placeholders but no context-mapping object.
    const templateMapping = template.template_context_mapping || contextMapping || {};
    const templateButtons = getTemplateButtons(template);
    const templateVariables = getTemplateVariables(template);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                ...sx,
            }}
        >
            {/* Template name + usage type — hideTitle skips this header when
                the calling context already shows the name (e.g. the edit
                dialog, which has the name/category/usage chips at the top of
                the form). */}
            {!hideTitle && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        maxWidth: 400,
                        mb: 2,
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}>
                        {formatTemplateName(template.name)}
                    </Typography>
                    <Chip
                        label={formatUsageType(template.usage_type)}
                        size="small"
                        sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            bgcolor: getUsageTypeColor(template.usage_type, theme).bg,
                            color: getUsageTypeColor(template.usage_type, theme).color,
                        }}
                    />
                </Box>
            )}

            {/* Phone mockup */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 250,
                }}
            >
                {/* WhatsApp header bar */}
                <Box
                    sx={{
                        bgcolor: WA_GREEN,
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.common.white, 0.2),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Icon icon="mdi:account" width={18} color={theme.palette.common.white} />
                    </Box>
                    <Box>
                        <Typography sx={{ color: theme.palette.common.white, fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.2 }}>
                            Template Preview
                        </Typography>
                        <Typography sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: '0.68rem', lineHeight: 1.2 }}>
                            WhatsApp Business
                        </Typography>
                    </Box>
                </Box>

                {/* Chat area */}
                <Box
                    sx={{
                        bgcolor: WA_BG,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        p: 2,
                        flex: 1,
                        '& .template-variable': {
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            color: theme.palette.info.dark,
                            px: 0.5,
                            py: 0.125,
                            borderRadius: 0.5,
                            fontWeight: 600,
                            fontSize: '0.82em',
                            fontFamily: 'monospace',
                        },
                    }}
                >
                    {/* Message bubble */}
                    <Box
                        sx={{
                            bgcolor: WA_BUBBLE,
                            borderRadius: '0 12px 12px 12px',
                            p: 1.5,
                            maxWidth: '92%',
                            position: 'relative',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: -8,
                                width: 0,
                                height: 0,
                                borderTop: `8px solid ${WA_BUBBLE}`,
                                borderLeft: '8px solid transparent',
                            },
                        }}
                    >
                        {getTemplateHeader(template) && (
                            <Typography
                                sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.85rem', color: theme.palette.grey[800] }}
                                dangerouslySetInnerHTML={{
                                    __html: renderTextWithVariables(getTemplateHeader(template), templateMapping),
                                }}
                            />
                        )}
                        <Typography
                            sx={{
                                fontSize: '0.85rem',
                                lineHeight: 1.55,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: theme.palette.grey[800],
                            }}
                            dangerouslySetInnerHTML={{
                                __html: renderTextWithVariables(getTemplateBody(template), templateMapping),
                            }}
                        />
                        {getTemplateFooter(template) && (
                            <Typography
                                sx={{ display: 'block', mt: 0.75, fontSize: '0.75rem', color: '#8696A0' }}
                                dangerouslySetInnerHTML={{
                                    __html: renderTextWithVariables(getTemplateFooter(template), templateMapping),
                                }}
                            />
                        )}
                        <Typography
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                                mt: 0.5,
                                color: '#8696A0',
                                fontSize: '0.62rem',
                            }}
                        >
                            Just now
                            <Icon icon="mdi:check-all" width={14} color="#53BDEB" />
                        </Typography>
                    </Box>

                    {/* CTA buttons — outside bubble, like WhatsApp */}
                    {templateButtons.length > 0 && (
                        <Box sx={{ maxWidth: '92%', mt: 0.5 }}>
                            {templateButtons.map((btn, idx) => {
                                let btnIcon = 'mdi:reply';
                                if (btn.type === 'URL') btnIcon = 'mdi:open-in-new';
                                else if (btn.type === 'PHONE_NUMBER') btnIcon = 'mdi:phone';
                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            bgcolor: WA_BUBBLE,
                                            borderRadius: 2,
                                            mt: 0.5,
                                            py: 0.75,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.75,
                                            color: theme.palette.info.dark,
                                            fontSize: '0.82rem',
                                            fontWeight: 500,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                            cursor: 'default',
                                        }}
                                    >
                                        <Icon icon={btnIcon} width={14} />
                                        {btn.text}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Template variables legend */}
            {showVariables && templateVariables.length > 0 && (
                <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.62rem',
                            mb: 0.75,
                        }}
                    >
                        Template Variables
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {templateVariables.map((varNum) => {
                            const mappedValue = templateMapping[varNum];
                            return (
                                <Chip
                                    key={varNum}
                                    size="small"
                                    label={mappedValue || `var${varNum}`}
                                    variant="outlined"
                                    sx={{
                                        height: 24,
                                        fontSize: '0.72rem',
                                        fontWeight: 500,
                                        borderColor: theme.palette.grey[300],
                                        bgcolor: 'background.paper',
                                        '& .MuiChip-label': { px: 1 },
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

WhatsAppTemplatePreviewPanel.propTypes = {
    template: PropTypes.object,
    contextMapping: PropTypes.object,
    showVariables: PropTypes.bool,
    hideTitle: PropTypes.bool,
    sx: PropTypes.object,
};

WhatsAppTemplatePreviewPanel.defaultProps = {
    template: null,
    contextMapping: {},
    showVariables: true,
    hideTitle: false,
    sx: {},
};
